import { useEffect, useRef, useState } from 'react';
import { t } from '../i18n/index.ts';

/**
 * The chapters, in the order the work is actually done: find it, cut it, get
 * the file, and only then the text. Each step is one thing a producer does,
 * written as an instruction rather than a description of a feature.
 */
const CHAPTERS = [
  {
    key: 'cut' as const,
    title: 'help.cut.title' as const,
    steps: ['help.cut.1', 'help.cut.2', 'help.cut.3'] as const,
  },
  {
    key: 'download' as const,
    title: 'help.download.title' as const,
    steps: ['help.download.1', 'help.download.2'] as const,
  },
  {
    key: 'transcribe' as const,
    title: 'help.transcribe.title' as const,
    steps: [
      'help.transcribe.1', 'help.transcribe.2', 'help.transcribe.3', 'help.transcribe.4',
    ] as const,
  },
  {
    key: 'video' as const,
    title: 'help.video.title' as const,
    steps: [
      'help.video.1', 'help.video.2', 'help.video.3',
      'help.video.4', 'help.video.5', 'help.video.6',
    ] as const,
  },
  {
    key: 'more' as const,
    title: 'help.more.title' as const,
    steps: ['help.more.1', 'help.more.2', 'help.more.3', 'help.more.4', 'help.more.5'] as const,
  },
];

/**
 * Flattened once, so "next" walks the whole guide rather than one chapter, and
 * every key is written out rather than built — which is what lets the typed
 * `t()` prove at build time that each step actually exists in ar.json.
 */
const PAGES = CHAPTERS.flatMap((chapter) =>
  chapter.steps.map((key, i) => ({ chapter: chapter.key, key, index: i + 1 })),
);

/**
 * A guide that stays within reach.
 *
 * Pinned to the edge rather than shown once on first run, because the person
 * who needs it is mid-shift and has already dismissed whatever appeared on
 * day one. It opens where it is, over the page, and closes back to a tab —
 * nothing about the archive moves to make room for it.
 */
export interface HelpGuideProps {
  /**
   * The archive window, in hours.
   *
   * Passed in rather than written into the guide, because it is configuration
   * (a stream's retention_hours) and a number repeated in prose goes stale the
   * first time somebody changes it -- which is exactly what happened when the
   * window moved from 12 hours to 24 and two sentences here still said 12.
   */
  retentionHours: number;
}

export function HelpGuide({ retentionHours }: HelpGuideProps) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const panel = useRef<HTMLDivElement>(null);
  const current = PAGES[page]!;
  const last = page === PAGES.length - 1;

  // Escape closes it, the way every other overlay in the app behaves.
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') setOpen(false);
      if (event.key === 'ArrowLeft') setPage((p) => Math.min(p + 1, PAGES.length - 1));
      if (event.key === 'ArrowRight') setPage((p) => Math.max(p - 1, 0));
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) panel.current?.focus();
  }, [open]);

  return (
    <>
      <button
        type="button"
        className="help-tab"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        <span className="help-tab-mark" aria-hidden="true">{t('help.mark')}</span>
        <span className="help-tab-label">{t('help.open')}</span>
      </button>

      {open && (
        <div
          className="help-panel"
          role="dialog"
          aria-label={t('help.title')}
          tabIndex={-1}
          ref={panel}
        >
          <div className="help-head">
            <span className="help-title">{t('help.title')}</span>
            <button type="button" className="help-close" onClick={() => setOpen(false)}>
              {t('help.close')}
            </button>
          </div>

          {/* Chapters are clickable, so someone who only needs transcription
              does not have to walk through cutting to reach it. */}
          <nav className="help-chapters">
            {CHAPTERS.map((chapter) => {
              const first = PAGES.findIndex((p) => p.chapter === chapter.key);
              const active = current.chapter === chapter.key;
              return (
                <button
                  key={chapter.key}
                  type="button"
                  className={active ? 'selected' : ''}
                  onClick={() => setPage(first)}
                >
                  {t(chapter.title)}
                </button>
              );
            })}
          </nav>

          <div className="help-body">
            <span className="help-step-number mono">{current.index}</span>
            <p className="help-step">{t(current.key, { hours: retentionHours })}</p>
          </div>

          <div className="help-foot">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(p - 1, 0))}
            >
              {t('help.prev')}
            </button>
            <span className="help-progress mono">
              {t('help.step_of')
                .replace('{current}', String(page + 1))
                .replace('{total}', String(PAGES.length))}
            </span>
            <button
              type="button"
              className="primary"
              onClick={() => (last ? setOpen(false) : setPage((p) => p + 1))}
            >
              {last ? t('help.done') : t('help.next')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
